import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  IssueService,
  searchIssuesService,
} from '../issues/issueService';
import { getProperty } from '../fsConfig';
import {
  eachResult,
  formatResolveByTimes,
} from '../lib/sharedSearchService';
import { collectGarbage } from '../configTdm';
import IssueIcon from '../issues/issueIcon';
import { Issue } from './interfaces';
import FullPageLoadingSpinner from '../pages/components/fullPageLoadingSpinner';

type IssuesState = {
  total: number;
  issues: Issue[];
};

const Flyout: React.FC = () => {
  const pollTimerRef = useRef<NodeJS.Timeout | null>(null);
  const [my, setMy] = useState<IssuesState>({ total: 0, issues: [] });
  const [open, setOpen] = useState<IssuesState>({ total: 0, issues: [] });
  const [showSpinner, setShowSpinner] = useState(false);

  useEffect(() => {
    pollForData();
    getData();

    return () => {
      stopPolling();
      collectGarbage();
      setMy({ total: 0, issues: [] });
      setOpen({ total: 0, issues: [] });
    };
  }, []);

  const pollForData = useCallback(() => {
    stopPolling();
    pollTimerRef.current = setInterval(() => {
      getData();
    }, 60 * 1000);
  }, []);

  const stopPolling = useCallback(() => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  const getNumOfIssues = () => {
    let rowHeight = 60;
    let headerHeight = 40;
    let rows = window.innerHeight;
    rows -= headerHeight * 2;
    rows /= rowHeight;
    return Math.floor(rows);
  };

  const getData = useCallback(() => {
    setShowSpinner(true);
    let numOfIssues = getNumOfIssues();
    if (numOfIssues) {
      searchIssuesService
        .fetch(
          'state=' +
          encodeURIComponent('open,assisting') +
          '&sortBy=time&assignee=' +
          getProperty('session', {}).name,
        )
        .then(myIssues => {
          const myIssuesLists = eachResult(myIssues.data.issues, [
            formatResolveByTimes,
          ]).slice(0, 5);
          setMy({
            total: myIssues.data.totalIssuesFound,
            issues: myIssuesLists,
          });
          numOfIssues -= myIssuesLists.length;
          searchIssuesService.fetch('state=open&sortBy=time').then(issues => {
            const openIssues = eachResult(issues.data.issues, [
              formatResolveByTimes,
            ]).slice(0, numOfIssues);
            setOpen({
              total: issues.data.totalIssuesFound,
              issues: openIssues,
            });
            setShowSpinner(false);
          }).catch(() => {
            setShowSpinner(false);
          });
        })
        .catch(() => {
          setShowSpinner(false);
        });
    }
  }, []);

  const show = useCallback(
    (action: string, tripId?: string, issueId?: string) => {
      let url;
      switch (action) {
        case 'open':
          url = '/issues/open';
          break;

        case 'my':
          url = '/issues/my';
          break;

        case 'view':
          url = `/trip/${tripId}#issue-${issueId}`;
          break;

        default:
          console.warn(
            '[IssuesFlyoutCtrl] Unknown storage event action.',
            action,
          );
          break;
      }

      window.open(url);
    },
    [],
  );

  const viewTripIssue = useCallback(
    (tripId?: string | number, issueId?: string) => {
      show('view', String(tripId), issueId);
    },
    [show],
  );

  const viewOpenIssues = useCallback(() => {
    show('open');
  }, [show]);

  const viewMyIssues = useCallback(() => {
    show('my');
  }, [show]);

  const assistIssue = useCallback(
    (tripId?: string | number, issueId?: string) => {
      if (tripId && issueId) {
        stopPolling();
        IssueService.changeState([{ id: issueId, tripId }], 'ASSISTING').then(
          resp => {
            if (
              resp?.status === 200 &&
              resp?.data?.succeeded != null &&
              resp?.data?.requests === resp.data.succeeded
            ) {
              getData();
              show('view', String(tripId), issueId);
            }
            pollForData();
          },
          () => {
            pollForData();
          },
        );
      }
    },
    [stopPolling, getData, show, pollForData],
  );

  const setOpenAssistHover = useCallback(
    (flag: boolean, parentIndex: number) => {
      const issuesLists = open.issues.map((issue, index) => {
        if (parentIndex === index) {
          return { ...issue, openAssistHover: flag };
        }
        return issue;
      });
      setOpen({ ...open, issues: issuesLists });
    },
    [open],
  );

  const setMyAssistHover = useCallback(
    (flag: boolean, parentIndex: number) => {
      const issuesLists = my.issues.map((issue, index) => {
        if (parentIndex === index) {
          return { ...issue, myAssistHover: flag };
        }
        return issue;
      });
      setMy({ ...my, issues: issuesLists });
    },
    [my],
  );

  const Header: React.FC<{
    headerText: string;
    issueLen: number;
    issueTotal: number;
    action: () => void;
  }> = ({ headerText, issueLen, issueTotal, action }) => (
    <div className="row header">
      <div className="col-xs-9">
        <span className="title">{headerText}</span>&nbsp;
        <span className="detail">
          Showing {issueLen} of {issueTotal}
        </span>
      </div>
      <div className="col-xs-3 maximize">
        <a href="#" onClick={action}>
          VIEW ALL
        </a>
      </div>
    </div>
  );

  const IssueList: React.FC<{
    issues: Issue[];
    flag: string;
    keyName: string;
    action: (flag: boolean, index: number) => void;
  }> = ({ issues, flag, keyName, action }) => (
    <>
      {issues && (
        <div className="row">
          <div className="col-xs-12">
            <table className="table table-striped table-hover issues-results">
              <tbody>
                {issues.map((issue, index) => (
                  <tr key={issue.id}>
                    <td
                      className={`date ${issue.resolveByTimeIsCritical ? 'critical' : ''
                        }`}
                      onClick={() => viewTripIssue(issue.tripId, issue.id)}
                    >
                      {issue.resolveByTimeFormatted}
                    </td>
                    <td
                      className="rec-trip-group"
                      onClick={() => viewTripIssue(issue.tripId, issue.id)}
                    >
                      <div className="row">
                        <div className="col-xs-12 record-locator">
                          {issue.recordLocator && issue.recordLocator.length > 15
                            ? `${issue.recordLocator.substring(0, 15)}...`
                            : issue.recordLocator}
                        </div>
                        <div className="col-xs-12 trip-group">
                          {issue.tripGroupName}
                        </div>
                      </div>
                    </td>
                    <td
                      className="icon"
                      onClick={() => viewTripIssue(issue.tripId, issue.id)}
                    >
                      <IssueIcon type={issue.type} resolveByTime={issue.resolveByTime}></IssueIcon>
                    </td>
                    <td
                      className="icon"
                      onClick={() => {
                        if (
                          (flag === 'myIssue' && issue.state === 'OPEN') ||
                          flag !== 'myIssue'
                        ) {
                          assistIssue(issue.tripId, issue.id);
                        }
                      }}
                    >
                      {((flag === 'myIssue' && issue.state === 'OPEN') ||
                        flag !== 'myIssue') && (
                          <div className="action-bar">
                            <div
                              className={`sprites action ${issue[keyName]
                                  ? 'sprite-assist-selected-24'
                                  : 'sprite-assist-normal-24'
                                }`}
                              onMouseEnter={() => action(true, index)}
                              onMouseLeave={() => action(false, index)}
                              title="Assist"
                            ></div>
                          </div>
                        )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );

  return (
    <>
      {showSpinner && <FullPageLoadingSpinner></FullPageLoadingSpinner>}
    <div className="container-fluid">
        <div id="content" className="content">
          <div className="flyout">
            <Header headerText="MY ISSUES" issueLen={my.issues.length} issueTotal={my.total} action={viewMyIssues} />
            <IssueList issues={my.issues} flag="myIssue" keyName="myAssistHover" action={setMyAssistHover} />
            <Header headerText="OPEN ISSUES" issueLen={open.issues.length} issueTotal={open.total} action={viewOpenIssues} />
            <IssueList issues={open.issues} flag="openIssue" keyName="openAssistHover" action={setOpenAssistHover} />
          </div>
        </div>
      </div>
    </>
  );
};

export default Flyout;
